import { FC, ReactElement, useEffect, useMemo, useState } from "react"
import { ECloudRole, ESyncDisposition, ICloudDevice, ICloudUser } from "../../types"
import Table, { ColumnsType } from "antd/es/table"
import { Translated } from "../../utils/translated"
import {
  Avatar,
  Button,
  Col,
  Image,
  Input,
  Popconfirm,
  Progress,
  Row,
  Tooltip,
  Typography,
  message,
} from "antd"
import { Link } from "react-router-dom"
import dayjs from "dayjs"
import { FlexCol, FlexRow } from "../../components/commons-ts/common"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faBoxArchive, faFlask } from "@fortawesome/free-solid-svg-icons"
import { Label } from "../../components/commons-ts/input"
import { MethodTypeEN1177, NormType, Site } from "../../generated/proto-ts/main"
import {
  COLOR_BG_DARK_BLUE,
  COLOR_BG_GREEN,
  COLOR_BG_LIGHT_BLUE,
  COLOR_BG_ORANGE,
  COLOR_BG_RED,
  COLOR_BG_ROW_DELETED,
  pbUUIDToUuid,
} from "../../utils/utils"
import {
  CheckCircleFilled,
  CheckCircleOutlined,
  CloudDownloadOutlined,
  CloudOutlined,
  CloudUploadOutlined,
  DeleteFilled,
  FilePdfOutlined,
  MinusSquareOutlined,
  PlusSquareOutlined,
  StopFilled,
  StopOutlined,
} from "@ant-design/icons"
import { useSyncronizationContext } from "../../providers/syncronizationProvider"
import { UserAvatar } from "../../components/commons-ts/userWidget"
import { NormTypeAvatar } from "../../components/commons-ts/normTypeAvatar"
import { EntityFlagsAvatar } from "../../components/commons-ts/entityFlagsAvatar"

const renderAsLink = (value: any, site: Site) => {
  return (
    <Link
      to={{
        pathname: `/sites/${pbUUIDToUuid(site.uuid)}`,
      }}
      style={{
        textDecoration: "none",
      }}
    >
      {value}
    </Link>
  )
}

const SummarySitesTableSyncWidget: FC<{
  siteUUID: string
}> = ({ siteUUID }) => {
  const { aggregatedSyncProgressMap } = useSyncronizationContext()

  let siteProgressAndDisposition = aggregatedSyncProgressMap[siteUUID]
  //   if (siteProgressAndDisposition === undefined) {
  //     return null
  //   }

  let iconStyle = {
    fontSize: "inherit",
    color: "inherit",
  }
  let dispositionIcon = null
  let progressIcon = null

  if (
    siteProgressAndDisposition === undefined ||
    siteProgressAndDisposition.disposition === ESyncDisposition.REMOTE
  ) {
    dispositionIcon = (
      <Tooltip overlay={<Translated keyEn="In the Cloud" />}>
        <CloudOutlined
          style={{
            ...iconStyle,
            color: COLOR_BG_LIGHT_BLUE,
          }}
        />
      </Tooltip>
    )
  } else if (siteProgressAndDisposition.disposition === ESyncDisposition.LOCAL) {
    dispositionIcon = (
      <Tooltip overlay={<Translated keyEn="On the Device" />}>
        <CheckCircleOutlined
          style={{
            ...iconStyle,
            color: COLOR_BG_GREEN,
          }}
        />
      </Tooltip>
    )
  } else {
    // Both
    // Only show if progress down/up is 1
    if (
      siteProgressAndDisposition.progressDown === 1 ||
      siteProgressAndDisposition.progressUp === 1
    ) {
      dispositionIcon = (
        <Tooltip overlay={<Translated keyEn="Synced" />}>
          <CheckCircleFilled
            style={{
              ...iconStyle,
              color: COLOR_BG_GREEN,
            }}
          />
        </Tooltip>
      )
    }
  }

  if (siteProgressAndDisposition !== undefined) {
    if (siteProgressAndDisposition.progressUp > 0 && siteProgressAndDisposition.progressUp < 1) {
      progressIcon = (
        <Progress
          type="circle"
          size={30}
          percent={100 * siteProgressAndDisposition.progressUp}
          format={() => {
            return (
              <CloudUploadOutlined
                style={{
                  fontSize: "1.1rem",
                }}
              />
            )
          }}
        />
      )
    } else if (
      siteProgressAndDisposition.progressDown > 0 &&
      siteProgressAndDisposition.progressDown < 1
    ) {
      progressIcon = (
        <Progress
          type="circle"
          size={30}
          percent={100 * siteProgressAndDisposition.progressDown}
          format={() => {
            return (
              <CloudDownloadOutlined
                style={{
                  fontSize: "1.1rem",
                }}
              />
            )
          }}
        />
      )
    }
  }

  return (
    <FlexRow
      style={{
        fontSize: 30,
        justifyContent: "center",
      }}
    >
      {dispositionIcon}
      {progressIcon}
    </FlexRow>
  )
}

const SummarySitesTableUserWidget: FC<{
  siteUserUUID: string | null
}> = ({ siteUserUUID }) => {
  const { enterpriseUsers } = useSyncronizationContext()
  const [user, setUser] = useState<ICloudUser | null>(null)
  useEffect(() => {
    if (enterpriseUsers === null || siteUserUUID === null) {
      return
    }
    let _user = enterpriseUsers.find((u) => u.UUID === siteUserUUID)
    if (_user === undefined) {
      return
    }
    setUser(_user)
  }, [siteUserUUID, enterpriseUsers])

  return <UserAvatar user={user} />
}

const SummarySitesTableDeviceWidget: FC<{
  deviceSN: string | null
}> = ({ deviceSN }) => {
  const { enterpriseDevices } = useSyncronizationContext()
  const [remoteDeviceMap, setRemoteDeviceMap] = useState<Record<string, ICloudDevice> | null>(null)
  if (deviceSN === null) {
    return "N/A"
  }

  useEffect(() => {
    if (enterpriseDevices === null) {
      setRemoteDeviceMap(null)
      return
    }
    let _remoteDeviceMap: Record<string, ICloudDevice> = {}
    for (let device of enterpriseDevices ?? []) {
      _remoteDeviceMap[device.SerialNumber] = device
    }
    setRemoteDeviceMap(_remoteDeviceMap)
  }, [enterpriseDevices])

  let deviceSnStr = "N/A"
  let deviceSnBigInt = BigInt(deviceSN)
  if (deviceSnBigInt !== BigInt(0)) {
    deviceSnStr = deviceSnBigInt.toString(16).padStart(16, "0")
  }
  if (deviceSnBigInt !== BigInt(0) && remoteDeviceMap !== null) {
    let remoteDevice = remoteDeviceMap[deviceSnStr]
    if (remoteDevice !== undefined) {
      if (remoteDevice.Name) {
        deviceSnStr = remoteDevice.Name
      } else if (remoteDevice.SerialNumber) {
        deviceSnStr = `Largueur №${remoteDevice.SerialNumber}`
      }
    }
  }
  return (
    <Typography.Text
      style={{
        maxWidth: "150px",
      }}
      ellipsis={{
        tooltip: deviceSnStr,
      }}
    >
      {deviceSnStr}
    </Typography.Text>
  )
}

export const SitesSummaryTable: FC<{
  sites: Site[] | null
  onDeleteSite: (site: Site) => void
}> = ({ sites, onDeleteSite }) => {
  const [antdMessageCtx, antdMessageCtxHolder] = message.useMessage()

  const [filteredSites, setFilteredSites] = useState<Site[] | null>(null)
  const [searchValue, setSearchValue] = useState<string>("")

  const { enterpriseUsers, isAdmin } = useSyncronizationContext()

  useEffect(() => {
    if (sites === null) {
      setFilteredSites(null)
      return
    }

    let _site: Site[] = sites.filter((site) => isAdmin || site.deleted_at === 0)

    if (searchValue === "") {
      setFilteredSites(_site)
      return
    }

    const t = setTimeout(() => {
      let _filteredSites: Site[] = []
      let normalizedSearchValue = searchValue.toLowerCase().normalize()
      for (let site of _site) {
        if (site.mission_name.toLowerCase().normalize().includes(normalizedSearchValue)) {
          _filteredSites.push(site)
          continue
        }
        if (site.site_name.toLowerCase().normalize().includes(normalizedSearchValue)) {
          _filteredSites.push(site)
          continue
        }
        if (site.client_name.toLowerCase().normalize().includes(normalizedSearchValue)) {
          _filteredSites.push(site)
          continue
        }
      }
      _filteredSites.sort((a, b) => {
        return (b.execution_date ?? b.created_at) - (a.execution_date ?? a.created_at)
      })
      setFilteredSites(_filteredSites)
    }, 250)
    return () => {
      clearTimeout(t)
    }
  }, [isAdmin, searchValue, sites])

  const columns = useMemo((): ColumnsType<Site> => {
    return [
      {
        title: (
          <div style={{ maxWidth: 40 }}>
            <Typography.Text
              ellipsis={{
                tooltip: <Translated keyEn="User" />,
              }}
            >
              <Translated keyEn="User" />
            </Typography.Text>
          </div>
        ),
        render: (_, site) => {
          return <SummarySitesTableUserWidget siteUserUUID={pbUUIDToUuid(site.user_uuid)} />
        },
      },
      {
        title: (
          <div style={{ maxWidth: 60 }}>
            <Typography.Text
              ellipsis={{
                tooltip: <Translated keyEn="Norm" />,
              }}
            >
              <Translated keyEn="Norm" />
            </Typography.Text>
          </div>
        ),
        render: (_, site) => {
          return <NormTypeAvatar site={site} />
        },
        sorter: (a, b) => {
          return b.norm_type - a.norm_type
        },
        filters: [
          {
            text: <Translated keyEn="Playground floor" />,
            value: NormType.EN_1177,
          },
          {
            text: <Translated keyEn="Sports mat" />,
            value: NormType.EN_12503,
          },
          {
            text: <Translated keyEn="Airbags" />,
            value: NormType.EN_14960,
          },
          {
            text: <Translated keyEn="Trampoline" />,
            value: NormType.EN_ISO_23659,
          },
        ],
        onFilter: (value, record) => {
          return record.norm_type === value
        },
      },
      {
        title: <Translated keyEn="Title" />,
        render: (_, site) => {
          let _title = site.site_name
          let _subtitle = ""
          if (site.mission_name) {
            _subtitle += `${site.mission_name}`
          }
          if (site.client_name) {
            _subtitle += ` @ ${site.client_name}`
          }
          let _content = <></>
          let el = (
            <div style={{ maxWidth: 200 }}>
              <Typography.Text
                ellipsis={{
                  tooltip: (
                    <>
                      {_title}
                      <br />
                      {_subtitle}
                    </>
                  ),
                }}
              >
                <b style={{ fontSize: "1.1rem" }}>{_title}</b>
                <br />
                <i style={{ whiteSpace: "nowrap" }}>{_subtitle}</i>
              </Typography.Text>
            </div>
          )
          return renderAsLink(el, site)
        },
        sorter: (a, b) => {
          return a.site_name.localeCompare(b.site_name)
        },
      },
      {
        title: <Translated keyEn="Sync" />,
        render: (_, site) => {
          return <SummarySitesTableSyncWidget siteUUID={pbUUIDToUuid(site.uuid)} />
        },
      },
      {
        title: <Translated keyEn="Device" />,
        render: (_, site) => {
          return <SummarySitesTableDeviceWidget deviceSN={site.device_sn} />
        },
      },
      {
        title: <Translated keyEn="Date" />,
        render: (_, site) => {
          if (site.execution_date === 0) {
            let formattedDate = dayjs(site.created_at).format("YYYY-MM-DD")
            return <i style={{ whiteSpace: "nowrap" }}>{formattedDate}</i>
          } else {
            let formattedDate = dayjs(site.execution_date).format("YYYY-MM-DD")
            return <i style={{ whiteSpace: "nowrap" }}>{formattedDate}</i>
          }
        },
        sorter: (a, b) => {
          let dateA = a.execution_date || a.created_at
          let dateB = b.execution_date || b.created_at
          return dateA - dateB
        },
        sortDirections: ["descend", "ascend"],
        defaultSortOrder: "descend",
      },
      {
        title: (
          <div style={{ maxWidth: 50 }}>
            <Typography.Text ellipsis={{ tooltip: <Translated keyEn="Flags" /> }}>
              <Translated keyEn="Flags" />
            </Typography.Text>
          </div>
        ),
        render: (_, site) => {
          return <EntityFlagsAvatar entity={site} />
        },
      },
      {
        title: "",
        width: "1rem",
        render: (_, site) => {
          return (
            <FlexRow
              style={{
                justifyContent: "center",
                alignItems: "center",
                gap: 0,
              }}
            >
              {/* Report */}
              <Tooltip overlay={<Translated keyEn="Generate report" />}>
                <Link to={`/report/${pbUUIDToUuid(site.uuid)}`}>
                  <Button type="link" icon={<FilePdfOutlined />} style={{ width: 25 }} />
                </Link>
              </Tooltip>
              {/* Delete */}
              <Tooltip overlay={<Translated keyEn="Delete" />}>
                <Popconfirm
                  title="Are you sure to delete this site?"
                  okText="Yes"
                  okButtonProps={{
                    danger: true,
                  }}
                  cancelText="No"
                  onConfirm={() => {
                    onDeleteSite(site)
                  }}
                  placement="left"
                >
                  <Button type="link" danger icon={<DeleteFilled />} style={{ width: 25 }} />
                </Popconfirm>
              </Tooltip>
            </FlexRow>
          )
        },
      },
    ]
  }, [])

  return (
    <FlexCol>
      {antdMessageCtxHolder}
      <FlexCol style={{ gap: 0 }}>
        <Label>
          <Translated keyEn={"Universal search"} />
        </Label>
        <Input
          size="large"
          value={searchValue ?? ""}
          placeholder={"Search for mission, site, client.."}
          onChange={(value) => {
            setSearchValue(value.target.value)
          }}
          variant="filled"
        />
      </FlexCol>
      <Table
        size="small"
        dataSource={filteredSites ?? []}
        loading={sites === null}
        columns={columns}
        pagination={{
          defaultPageSize: 20,
          hideOnSinglePage: true,
        }}
        expandable={{
          expandIcon: ({ expanded, onExpand, record }) => {
            if (expanded) {
              return <MinusSquareOutlined onClick={(e) => onExpand(record, e)} />
            } else {
              return <PlusSquareOutlined onClick={(e) => onExpand(record, e)} />
            }
          },
          expandedRowRender: (site) => {
            let user = enterpriseUsers?.find((u) => u.UUID === pbUUIDToUuid(site.user_uuid))
            return (
              <>
                <Row gutter={[10, 10]}>
                  <Col xs={24} md={12}>
                    <Label>
                      <Translated keyEn="Details" />
                    </Label>
                    <ul>
                      {/* <li>
                                                <b>UUID:</b>{" "}
                                                {pbUUIDToUuid(site.uuid)}
                                            </li> */}
                      <li>
                        <b>
                          <Translated keyEn="Created at" />
                        </b>{" "}
                        {dayjs(site.created_at).format("YYYY-MM-DD HH:mm")}
                      </li>
                      <li>
                        <b>
                          <Translated keyEn="Updated at" />
                        </b>{" "}
                        {dayjs(site.updated_at).format("YYYY-MM-DD HH:mm")}
                      </li>
                      <li>
                        <b>
                          <Translated keyEn="Executed at" />
                        </b>{" "}
                        {dayjs(site.execution_date).format("YYYY-MM-DD HH:mm")}
                      </li>
                      {user !== undefined && (
                        <li>
                          <b>
                            <Translated keyEn="User" />:{" "}
                          </b>
                          {user.FirstName} {user.LastName} ({user.Enterprise.Name})
                        </li>
                      )}
                      {site.address && (
                        <li>
                          <b>
                            <Translated keyEn="Address" />
                          </b>{" "}
                          {site.address}
                        </li>
                      )}
                    </ul>
                  </Col>
                  <Col xs={24} md={12}>
                    <Label>
                      <Translated keyEn="Picture" />
                    </Label>
                    {site.pictures.length > 0 && (
                      <Image
                        src={`/api/uploads/${pbUUIDToUuid(site.pictures[0])}`}
                        height="100px"
                      />
                    )}
                  </Col>
                </Row>
              </>
            )
          },
        }}
        rowKey={(site) => pbUUIDToUuid(site.uuid)} // site.uuid
        scroll={{ x: true }}
        style={{
          width: "100%",
        }}
        bordered
        onRow={(record) => {
          if (record.deleted_at === 0) {
            return {}
          }
          if (isAdmin) {
            return {
              style: {
                backgroundColor: COLOR_BG_ROW_DELETED,
              },
            }
          } else {
            return {
              style: {
                display: "none",
              },
            }
          }
        }}
      />
    </FlexCol>
  )
}
